import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { getRoles } from 'utils/helperFns'
import { isFederalDealQueryVar } from 'cache/federal-deal-query'
import { latestDealVar } from 'cache/latest-deal-info'
import DecisionComponent from 'components/DecisionPage/DecisionComponent'
import { useGetApproverGroups } from 'hooks/use-approver-group'
import { useCheckSubmitValidationCriteria } from 'hooks/use-check-submit-validation'
import { useDealLoadStatus } from 'hooks/use-deal-load-status'
import { useGetDecisionEmail } from 'hooks/use-get-email-content'
import { useLatestDealVersion } from 'hooks/use-latest-deal-version'
import { useLatestSubmit } from 'hooks/use-latest-submit'
import { useGetUserProfile } from 'hooks/use-profile'
import { useSearchDeal } from 'hooks/use-search-deal'
import { useUserProfile } from 'hooks/use-user-profile'
import { useClearDecisionPageCache } from 'queries/clear-decision-page-cache'
import { IDeal } from 'queries/searchDeal'

const HomePage = () => {
  const { dealNumber, emailId } = useParams<{ dealNumber: string; emailId: string }>()
  const decisionType = localStorage.getItem('decisionType')
  const decisionReason = localStorage.getItem('decisionReason')
  const {
    getDecisionEmail,
    data: emailConent,
    loading: emailContentLoading,
    versionStatus,
  } = useGetDecisionEmail()
  const { employeeNumber, userEmail, userId } = useUserProfile()
  const { handleGetApproverGroups, data: approverGroupsData } = useGetApproverGroups()
  const {
    getLatestDealVersion,
    data: latestDealVersion,
    loading: latestDealVersionLoading,
    error: latestDealVersionError,
  } = useLatestDealVersion()
  const {
    getLatestDealSubmit,
    data: latestDealSubmit,
    loading: latestSubmitLoading,
    error: latestSubmitError,
  } = useLatestSubmit()
  const {
    searchDeal,
    data: searchDealData,
    federalDeal,
    loading: searchDealLoading,
    error: searchDealError,
  } = useSearchDeal()
  const { checkSubmitValidationCriteria, loading: checkSubmitValidationCriteriaLoading } =
    useCheckSubmitValidationCriteria()
  const fedQuery = useReactiveVar(isFederalDealQueryVar)
  const [isInitialRender, setIsInitialRender] = useState(true)
  const [isActiveApprover, setIsActiveApprover] = useState(false)
  const { dealId: latestDealId } = latestDealVar()
  const FEDERAL_DEAL = 'HPF'
  const DEFAULT_SEARCH = 'HPIHPF'
  const { getUserProfile } = useGetUserProfile()
  const { fetchDealLoadStatus } = useDealLoadStatus()
  const { clearCache } = useClearDecisionPageCache()
  const fetchDealData = () => {
    if (!searchDealLoading && !searchDealError && dealNumber && fedQuery && isInitialRender) {
      if (!latestDealVersion && !latestDealVersionLoading && !latestDealVersionError) {
        getLatestDealVersion()
      }
      if (latestDealVersion) {
        searchDeal(DEFAULT_SEARCH)
        getLatestDealSubmit()
      }
    }
  }

  const dealInfo =
    searchDealData?.searchDeal?.deals && searchDealData.searchDeal.deals.length > 0
      ? searchDealData.searchDeal.deals[searchDealData.searchDeal.deals.length - 1]
      : undefined

  useEffect(() => {
    if (dealNumber && isInitialRender) {
      searchDeal(FEDERAL_DEAL)
    }
  }, [dealNumber])
  useEffect(() => {
    if (searchDealData && !federalDeal && isInitialRender) {
      fetchDealData()
    }
  }, [
    searchDealLoading,
    searchDealError,
    searchDealData,
    latestDealVersionLoading,
    latestDealVersion,
    latestDealVersionError,
    latestDealSubmit,
    latestSubmitLoading,
    latestSubmitError,
    checkSubmitValidationCriteriaLoading,
    employeeNumber,
    fedQuery,
  ])
  useEffect(() => {
    if (
      latestDealVersion?.latestDeal?.dealId &&
      employeeNumber &&
      userEmail &&
      searchDealData &&
      !federalDeal
    ) {
      checkSubmitValidationCriteria(latestDealVersion.latestDeal.dealId, employeeNumber, userEmail)
      setIsInitialRender(false)
    }
    if (userEmail) {
      getUserProfile({ variables: { email: userEmail, client_id: 'price_hub' } })
    }
  }, [latestDealVersion, employeeNumber, userEmail])

  useEffect(() => {
    if (latestDealId && decisionType) {
      handleGetApproverGroups(latestDealId, decisionType)
    }
    if (latestDealId && emailId) {
      getDecisionEmail({
        variables: {
          input: {
            dealId: latestDealId || 0,
            emailId,
          },
        },
      })
    }
    if (latestDealId && userId) {
      const clearCacheAndFetchDealLoadStatus = async () => {
        try {
          await clearCache({ variables: { dealId: latestDealId } })
          await fetchDealLoadStatus(latestDealId, userId)
        } catch (error) {
          console.error('Error clearing cache or fetching deal load status:', error)
        }
      }
      clearCacheAndFetchDealLoadStatus()
    }
  }, [latestDealId, decisionType])

  useEffect(() => {
    if (approverGroupsData?.length) {
      const approverlist = getRoles(approverGroupsData)
      const isActiveApprover = approverlist.find((approver: any) => {
        return (
          approver?.approverEmail === userEmail &&
          approver?.decisionTypeCode === decisionType &&
          approver?.decisionReasonCode === decisionReason
        )
      })?.isActiveApprover

      setIsActiveApprover(isActiveApprover ?? false)
    }
  }, [approverGroupsData])
  return (
    <div>
      <DecisionComponent
        isActiveApprover={isActiveApprover}
        emailContent={emailConent?.emailBody ?? ''}
        emailContentLoading={emailContentLoading}
        version={versionStatus ?? null}
        dealInfo={dealInfo as IDeal}
        emailTemplateId={emailConent?.emailTemplateId ?? 0}
      />
    </div>
  )
}

export default HomePage
